// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { Injectable } from '@angular/core';
import { forkJoin, map, Observable } from 'rxjs';
import { Analysis } from '../../../interfaces/analysis';
import { HceSvColorList } from '../../../interfaces/hce/hce-sv-colors';
import { HceAnalysisService } from '../hce-analysis/hce-analysis.service';
import { HceReferenceService } from '../hce-reference/hce-reference.service';
import { HceSvParserService } from '../hce-parser/parsers/hce-sv-parser/hce-sv-parser.service';
import { HceSvFilter } from '../../../interfaces/hce/hce-sv-filter';
import { HceAnnotation } from '../../../interfaces/hce/hce-annotation';
import { HceSvFilterService } from '../hce-sv-filter/hce-sv-filter.service';
import { SvResponse } from '../../../interfaces/hce/hce-sv';

export type HceSvParseOptions = {
  useRefsNameMapping?: boolean;
  useColorMap?: boolean;
  buildOverlapCache?: boolean;
};

@Injectable({ providedIn: 'root' })
export class HceSvService {

  constructor(
    private readonly analysis: HceAnalysisService,
    private readonly reference: HceReferenceService,
    private readonly parser: HceSvParserService,
    private readonly filter: HceSvFilterService,
  ) {}

  public getSvData(
    input: Analysis,
    filter: HceSvFilter,
    annotationsByFile: Record<string, HceAnnotation[]>,
    options?: HceSvParseOptions
  ): Observable<any[]> {
    const analysisId = input.AnalysisID;
    const panelId = input.PanelID;
    const useRefs = options?.useRefsNameMapping === true;
    const useColor = options?.useColorMap === true;
    const buildOverlapCache = options?.buildOverlapCache === true;
    const sv$ = this.analysis.getSvEnhancedHsvJson<SvResponse>(analysisId);
    const refs$ = this.reference.getRefsNameMappingJson(panelId);
    return forkJoin({
      svEnhancedHsv: sv$,
      refsNameMapping: refs$,
    }).pipe(
      map(({ svEnhancedHsv, refsNameMapping }) => {
        // Remove 'InDel' key from color list: parser expects canonical SV type keys only
        const { InDel, ...svColorList } = HceSvColorList;
        return this.parser.parse(
          this.filterSv(svEnhancedHsv, refsNameMapping, filter, annotationsByFile, buildOverlapCache),
          useRefs ? refsNameMapping : undefined,
          useColor ? svColorList : undefined,
        );
      }),
      map(parsed => [...parsed].sort((a, b) => a.start - b.start)),
    );
  }

  private filterSv(
    svEnhancedHsv: SvResponse,
    refsNameMapping: Record<string, string>,
    filter: HceSvFilter,
    annotationsByFile: Record<string, HceAnnotation[]>,
    buildOverlapCache: boolean,
  ): SvResponse {
    if (buildOverlapCache) {
      console.time('Annotation data: build overlap cache');
      this.filter.buildOverlapCache(
        svEnhancedHsv.svs,
        refsNameMapping,
        annotationsByFile
      );
      console.timeEnd('Annotation data: build overlap cache');
    }
    console.time('SV data: filter');
    const filterd = this.filter.filterSV(
      svEnhancedHsv.svs,
      refsNameMapping,
      filter,
      annotationsByFile
    );
    console.timeEnd('SV data: filter');
    return {
      svs: filterd,
      version: svEnhancedHsv.version,
      hce_version: svEnhancedHsv.hce_version
    };
  }
}
